import { c as _c } from "react/compiler-runtime";
import { Button, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger } from '@repo/ui/components';
import { useState } from 'react';
import type { Column, ColumnDataType, DataTableFilterActions, FilterModel, FilterOperators } from '../core';
import { isColumnOfType } from '../core';
import { dateFilterOperators, filterTypeOperatorDetails, locationFilterOperators, multiOptionFilterOperators, numberFilterOperators, optionFilterOperators, textFilterOperators } from '../core/operators';
import { type Locale, t } from '../lib/i18n';
interface FilterOperatorControllerProps<TData, TType extends ColumnDataType = ColumnDataType> {
  actions: DataTableFilterActions;
  closeController: () => void;
  column: Column<TData, TType>;
  filter: FilterModel<TType>;
  locale?: Locale;
}
interface FilterOperatorDisplayProps<TType extends ColumnDataType> {
  columnType: TType;
  filter: FilterModel<TType>;
  locale?: Locale;
}
interface FilterOperatorProps<TData, TType extends ColumnDataType> {
  actions: DataTableFilterActions;
  column: Column<TData, TType>;
  filter: FilterModel<TType>;
  locale?: Locale;
}

// Renders the filter operator display and menu for a given column filter
// The filter operator display is the label and icon for the filter operator
// The filter operator menu is the dropdown menu for the filter operator
export function FilterOperator(t0) {
  const $ = _c(26);
  const {
    actions,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const [open, setOpen] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setOpen(false);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const close = t2;
  let t3;
  if ($[1] !== column.type || $[2] !== filter || $[3] !== locale) {
    t3 = <PopoverTrigger asChild={true} data-sentry-element="PopoverTrigger" data-sentry-source-file="filter-operator.tsx"><Button className="m-0 h-full w-fit rounded-none p-0 px-2 text-xs whitespace-nowrap" variant="ghost" data-sentry-element="Button" data-sentry-source-file="filter-operator.tsx"><FilterOperatorDisplay columnType={column.type} filter={filter} locale={locale} data-sentry-element="FilterOperatorDisplay" data-sentry-source-file="filter-operator.tsx" /></Button></PopoverTrigger>;
    $[1] = column.type;
    $[2] = filter;
    $[3] = locale;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== locale) {
    t4 = t("search", locale);
    $[5] = locale;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <CommandInput placeholder={t4} data-sentry-element="CommandInput" data-sentry-source-file="filter-operator.tsx" />;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== locale) {
    t6 = t("noresults", locale);
    $[9] = locale;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="filter-operator.tsx">{t6}</CommandEmpty>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== actions || $[14] !== column || $[15] !== filter || $[16] !== locale) {
    t8 = <CommandList className="max-h-fit" data-sentry-element="CommandList" data-sentry-source-file="filter-operator.tsx"><FilterOperatorController actions={actions} closeController={close} column={column} filter={filter} locale={locale} data-sentry-element="FilterOperatorController" data-sentry-source-file="filter-operator.tsx" /></CommandList>;
    $[13] = actions;
    $[14] = column;
    $[15] = filter;
    $[16] = locale;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t5 || $[19] !== t7 || $[20] !== t8) {
    t9 = <PopoverContent align="start" className="w-fit origin-(--radix-popover-content-transform-origin) p-0" data-sentry-element="PopoverContent" data-sentry-source-file="filter-operator.tsx"><Command loop={true} data-sentry-element="Command" data-sentry-source-file="filter-operator.tsx">{t5}{t7}{t8}</Command></PopoverContent>;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== open || $[23] !== t3 || $[24] !== t9) {
    t10 = <Popover onOpenChange={setOpen} open={open} data-sentry-element="Popover" data-sentry-component="FilterOperator" data-sentry-source-file="filter-operator.tsx">{t3}{t9}</Popover>;
    $[22] = open;
    $[23] = t3;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}

/*
 *
 * TODO: Reduce into a single component. Each data type does not need it's own controller.
 *
 */
export function FilterOperatorController(t0) {
  const $ = _c(42);
  const {
    actions,
    closeController,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  if (isColumnOfType(column, "amount") && filter.type === "amount") {
    let t2;
    if ($[0] !== actions || $[1] !== closeController || $[2] !== column || $[3] !== filter || $[4] !== locale) {
      t2 = <FilterOperatorNumberController actions={actions} closeController={closeController} column={column} filter={filter} locale={locale} />;
      $[0] = actions;
      $[1] = closeController;
      $[2] = column;
      $[3] = filter;
      $[4] = locale;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  if (isColumnOfType(column, "date") && filter.type === "date") {
    let t2;
    if ($[6] !== actions || $[7] !== closeController || $[8] !== column || $[9] !== filter || $[10] !== locale) {
      t2 = <FilterOperatorDateController actions={actions} closeController={closeController} column={column} filter={filter} locale={locale} />;
      $[6] = actions;
      $[7] = closeController;
      $[8] = column;
      $[9] = filter;
      $[10] = locale;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    return t2;
  }
  if (isColumnOfType(column, "location") && filter.type === "location") {
    let t2;
    if ($[12] !== actions || $[13] !== closeController || $[14] !== column || $[15] !== filter || $[16] !== locale) {
      t2 = <FilterOperatorLocationController actions={actions} closeController={closeController} column={column} filter={filter} locale={locale} />;
      $[12] = actions;
      $[13] = closeController;
      $[14] = column;
      $[15] = filter;
      $[16] = locale;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    return t2;
  }
  if (isColumnOfType(column, "multiOption") && filter.type === "multiOption") {
    let t2;
    if ($[18] !== actions || $[19] !== closeController || $[20] !== column || $[21] !== filter || $[22] !== locale) {
      t2 = <FilterOperatorMultiOptionController actions={actions} closeController={closeController} column={column} filter={filter} locale={locale} />;
      $[18] = actions;
      $[19] = closeController;
      $[20] = column;
      $[21] = filter;
      $[22] = locale;
      $[23] = t2;
    } else {
      t2 = $[23];
    }
    return t2;
  }
  if (isColumnOfType(column, "number") && filter.type === "number") {
    let t2;
    if ($[24] !== actions || $[25] !== closeController || $[26] !== column || $[27] !== filter || $[28] !== locale) {
      t2 = <FilterOperatorNumberController actions={actions} closeController={closeController} column={column} filter={filter} locale={locale} />;
      $[24] = actions;
      $[25] = closeController;
      $[26] = column;
      $[27] = filter;
      $[28] = locale;
      $[29] = t2;
    } else {
      t2 = $[29];
    }
    return t2;
  }
  if (isColumnOfType(column, "option") && filter.type === "option") {
    let t2;
    if ($[30] !== actions || $[31] !== closeController || $[32] !== column || $[33] !== filter || $[34] !== locale) {
      t2 = <FilterOperatorOptionController actions={actions} closeController={closeController} column={column} filter={filter} locale={locale} />;
      $[30] = actions;
      $[31] = closeController;
      $[32] = column;
      $[33] = filter;
      $[34] = locale;
      $[35] = t2;
    } else {
      t2 = $[35];
    }
    return t2;
  }
  if (isColumnOfType(column, "text") && filter.type === "text") {
    let t2;
    if ($[36] !== actions || $[37] !== closeController || $[38] !== column || $[39] !== filter || $[40] !== locale) {
      t2 = <FilterOperatorTextController actions={actions} closeController={closeController} column={column} filter={filter} locale={locale} />;
      $[36] = actions;
      $[37] = closeController;
      $[38] = column;
      $[39] = filter;
      $[40] = locale;
      $[41] = t2;
    } else {
      t2 = $[41];
    }
    return t2;
  }
  return null;
}
export function FilterOperatorDisplay(t0) {
  const $ = _c(5);
  const {
    columnType,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const operator = filterTypeOperatorDetails[columnType][filter.operator];
  let t2;
  if ($[0] !== locale || $[1] !== operator.key) {
    t2 = t(operator.key, locale);
    $[0] = locale;
    $[1] = operator.key;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const label = t2;
  let t3;
  if ($[3] !== label) {
    t3 = <span className="text-muted-foreground" data-sentry-component="FilterOperatorDisplay" data-sentry-source-file="filter-operator.tsx">{label}</span>;
    $[3] = label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
export function FilterOperatorTextController(t0) {
  const $ = _c(8);
  const {
    actions,
    closeController,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const filterDetails = textFilterOperators[filter.operator];
  let t2;
  if ($[0] !== actions || $[1] !== closeController || $[2] !== column || $[3] !== filterDetails || $[4] !== locale) {
    const relatedFilters = Object.values(textFilterOperators).filter(o => o.target === filterDetails.target);
    const changeOperator = value => {
      actions?.setFilterOperator(column, value);
      closeController();
    };
    let t3;
    if ($[6] !== locale) {
      t3 = t("operators", locale);
      $[6] = locale;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = <CommandGroup heading={t3} data-sentry-element="CommandGroup" data-sentry-component="FilterOperatorTextController" data-sentry-source-file="filter-operator.tsx">{relatedFilters.map(r => <CommandItem key={r.value} onSelect={() => changeOperator(r.value)} value={r.value}>{t(r.key, locale)}</CommandItem>)}</CommandGroup>;
    $[0] = actions;
    $[1] = closeController;
    $[2] = column;
    $[3] = filterDetails;
    $[4] = locale;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function FilterOperatorDateController(t0) {
  const $ = _c(6);
  const {
    actions,
    closeController,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const filterDetails = dateFilterOperators[filter.operator];
  let t2;
  if ($[0] !== actions || $[1] !== closeController || $[2] !== column || $[3] !== filterDetails || $[4] !== locale) {
    const relatedFilters = Object.values(dateFilterOperators).filter(o => o.target === filterDetails.target);
    const changeOperator = value => {
      actions?.setFilterOperator(column, value);
      closeController();
    };
    t2 = <CommandGroup data-sentry-element="CommandGroup" data-sentry-component="FilterOperatorDateController" data-sentry-source-file="filter-operator.tsx">{relatedFilters.map(r => <CommandItem key={r.value} onSelect={() => changeOperator(r.value)} value={r.value}>{t(r.key, locale)}</CommandItem>)}</CommandGroup>;
    $[0] = actions;
    $[1] = closeController;
    $[2] = column;
    $[3] = filterDetails;
    $[4] = locale;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function FilterOperatorLocationController(t0) {
  const $ = _c(8);
  const {
    actions,
    closeController,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const currentOperator = locationFilterOperators[filter.operator];
  let t2;
  if ($[0] !== actions || $[1] !== closeController || $[2] !== column || $[3] !== currentOperator || $[4] !== locale) {
    const availableOperators = Object.values(locationFilterOperators).filter(operator => operator.target === currentOperator.target);
    const handleOperatorChange = operatorValue => {
      actions?.setFilterOperator(column, operatorValue);
      closeController();
    };
    let t3;
    if ($[6] !== locale) {
      t3 = t("operators", locale);
      $[6] = locale;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = <CommandGroup heading={t3} data-sentry-element="CommandGroup" data-sentry-component="FilterOperatorLocationController" data-sentry-source-file="filter-operator.tsx">{availableOperators.map(operator_0 => <CommandItem key={operator_0.value} onSelect={() => handleOperatorChange(operator_0.value)} value={operator_0.value}>{t(operator_0.key, locale)}</CommandItem>)}</CommandGroup>;
    $[0] = actions;
    $[1] = closeController;
    $[2] = column;
    $[3] = currentOperator;
    $[4] = locale;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function FilterOperatorMultiOptionController(t0) {
  const $ = _c(9);
  const {
    actions,
    closeController,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const filterDetails = multiOptionFilterOperators[filter.operator];
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["include", "exclude", "include any of", "exclude if any of"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const availableOperators = t2;
  let t3;
  if ($[1] !== actions || $[2] !== closeController || $[3] !== column || $[4] !== filterDetails || $[5] !== locale) {
    const relatedFilters = Object.values(multiOptionFilterOperators).filter(o => o.target === filterDetails.target && availableOperators.includes(o.value));
    const changeOperator = value => {
      actions?.setFilterOperator(column, value);
      closeController();
    };
    let t4;
    if ($[7] !== locale) {
      t4 = t("operators", locale);
      $[7] = locale;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = <CommandGroup heading={t4} data-sentry-element="CommandGroup" data-sentry-component="FilterOperatorMultiOptionController" data-sentry-source-file="filter-operator.tsx">{relatedFilters.map(r => <CommandItem key={r.value} onSelect={() => changeOperator(r.value)} value={r.value}>{t(r.key, locale)}</CommandItem>)}</CommandGroup>;
    $[1] = actions;
    $[2] = closeController;
    $[3] = column;
    $[4] = filterDetails;
    $[5] = locale;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function FilterOperatorNumberController(t0) {
  const $ = _c(25);
  const {
    actions,
    closeController,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const filterDetails = numberFilterOperators[filter.operator];
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["is", "is between", "is greater than", "is greater than or equal to", "is less than", "is less than or equal to"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const availableOperators = t2;
  let T0;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[1] !== actions || $[2] !== closeController || $[3] !== column || $[4] !== filterDetails || $[5] !== locale) {
    const relatedFilters = Object.values(numberFilterOperators).filter(o => o.target === filterDetails.target && availableOperators.includes(o.value));
    const changeOperator = value => {
      actions?.setFilterOperator(column, value);
      closeController();
    };
    t7 = "FilterOperatorNumberController";
    t8 = "filter-operator.tsx";
    T0 = CommandGroup;
    if ($[13] !== locale) {
      t3 = t("operators", locale);
      $[13] = locale;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t4 = "CommandGroup";
    t5 = "filter-operator.tsx";
    t6 = relatedFilters.map(r => <CommandItem key={r.value} onSelect={() => changeOperator(r.value)} value={r.value}>{t(r.key, locale)}</CommandItem>);
    $[1] = actions;
    $[2] = closeController;
    $[3] = column;
    $[4] = filterDetails;
    $[5] = locale;
    $[6] = T0;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    T0 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
  }
  let t9;
  if ($[15] !== T0 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t9 = <T0 heading={t3} data-sentry-element={t4} data-sentry-source-file={t5}>{t6}</T0>;
    $[15] = T0;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t10 = <div data-sentry-component={t7} data-sentry-source-file={t8}>{t9}</div>;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
function FilterOperatorOptionController(t0) {
  const $ = _c(8);
  const {
    actions,
    closeController,
    column,
    filter,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const filterDetails = optionFilterOperators[filter.operator];
  let t2;
  if ($[0] !== actions || $[1] !== closeController || $[2] !== column || $[3] !== filterDetails || $[4] !== locale) {
    const relatedFilters = Object.values(optionFilterOperators).filter(o => o.target === filterDetails.target);
    const changeOperator = value => {
      actions?.setFilterOperator(column, value);
      closeController();
    };
    let t3;
    if ($[6] !== locale) {
      t3 = t("operators", locale);
      $[6] = locale;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = <CommandGroup heading={t3} data-sentry-element="CommandGroup" data-sentry-component="FilterOperatorOptionController" data-sentry-source-file="filter-operator.tsx">{relatedFilters.map(r => <CommandItem key={r.value} onSelect={() => changeOperator(r.value)} value={r.value}>{t(r.key, locale)}</CommandItem>)}</CommandGroup>;
    $[0] = actions;
    $[1] = closeController;
    $[2] = column;
    $[3] = filterDetails;
    $[4] = locale;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}